"use client";

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Checkbox } from "~/components/ui/checkbox";
import { api } from "~/trpc/react";
import Cookies from "js-cookie";
import { trackWaitlistSubmit } from "~/lib/gtm";

interface WaitlistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const FEATURE_OPTIONS = [
  { id: "creative_optimization", label: "Creative Optimization" },
  { id: "campaign_creation", label: "Campaign Creation" },
  { id: "budget_allocation", label: "Budget Allocation" },
  { id: "other", label: "Other" },
] as const;

export function WaitlistDialog({ open, onOpenChange }: WaitlistDialogProps) {
  const [email, setEmail] = useState("");
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [otherFeatureText, setOtherFeatureText] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const joinMutation = api.waitlist.join.useMutation({
    onSuccess: (data) => {
      Cookies.set("waitlistJoined", "true", { expires: 365 });
      setIsSubmitted(true);
      setIsLoading(false);
      // Track GTM event
      trackWaitlistSubmit(email);
    },
    onError: (err) => {
      setError(err.message || "Failed to join waitlist");
      setIsLoading(false);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    if (!email) {
      setError("Email is required");
      setIsLoading(false);
      return;
    }

    // Filter out "other" from feature interests for the API
    const apiFeatureInterests = selectedFeatures.filter(f => f !== "other");

    joinMutation.mutate({
      email,
      featureInterests: apiFeatureInterests,
      otherFeatureInterest: selectedFeatures.includes("other") ? otherFeatureText : undefined,
    });
  };

  const handleFeatureChange = (featureId: string, checked: boolean) => {
    setSelectedFeatures((prev) =>
      checked ? [...prev, featureId] : prev.filter((f) => f !== featureId)
    );
  };

  const showOtherField = selectedFeatures.includes("other");

  if (isSubmitted) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-center text-green-400">
              Welcome to LeadSpector!
            </DialogTitle>
            <DialogDescription className="text-center text-slate-300 pt-4">
              You&apos;re on the waitlist. We&apos;ll be in touch soon with early access details.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-4">
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl">Join the Waitlist</DialogTitle>
          <DialogDescription className="text-slate-300">
            Be the first to experience AI-powered ad optimization.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-200">
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-500"
              disabled={isLoading}
            />
          </div>

          <div className="space-y-3">
            <Label className="text-slate-200">
              Which features interest you?
            </Label>
            <div className="space-y-3">
              {FEATURE_OPTIONS.map((option) => (
                <div key={option.id} className="flex items-center space-x-3">
                  <Checkbox
                    id={option.id}
                    checked={selectedFeatures.includes(option.id)}
                    onCheckedChange={(checked) =>
                      handleFeatureChange(option.id, checked === true)
                    }
                    disabled={isLoading}
                    className="border-slate-600 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                  />
                  <Label
                    htmlFor={option.id}
                    className="text-slate-300 font-normal cursor-pointer"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {showOtherField && (
            <div className="space-y-2">
              <Label htmlFor="otherFeature" className="text-slate-200">
                Tell us about other features you&apos;re interested in
              </Label>
              <Input
                id="otherFeature"
                placeholder="e.g., Competitor analysis, Reporting..."
                value={otherFeatureText}
                onChange={(e) => setOtherFeatureText(e.target.value)}
                className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-500"
                disabled={isLoading}
              />
            </div>
          )}

          {error && (
            <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md p-3">
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700"
            disabled={isLoading}
          >
            {isLoading ? "Joining..." : "Join Waitlist"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
